import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Finger / pointer signature pad. Returns a PNG data URL via onChange when ink exists.
 */
export default function SignaturePad({
  value = null,
  onChange,
  disabled = false,
  label = "Draw your signature",
  timestamp = null,
  "data-testid": testId = "signature-pad",
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [hasInk, setHasInk] = useState(Boolean(value));

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width === w && canvas.height === h) return;
    const prev = canvas.toDataURL("image/png");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#222222";
    ctx.lineWidth = 2.2;
    if (prev && prev !== "data:,") {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = prev;
    }
  }, []);

  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [resizeCanvas]);

  useEffect(() => {
    if (!value) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      setHasInk(false);
    }
  }, [value]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches?.[0] || e.changedTouches?.[0] || e;
    return {
      x: src.clientX - rect.left,
      y: src.clientY - rect.top,
    };
  };

  const emit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    setHasInk(true);
    onChange?.(dataUrl);
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    const pt = pointFromEvent(e);
    if (!pt) return;
    drawingRef.current = true;
    lastRef.current = pt;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(pt.x, pt.y);
    ctx.lineTo(pt.x + 0.01, pt.y + 0.01);
    ctx.stroke();
  };

  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const pt = pointFromEvent(e);
    const last = lastRef.current;
    if (!pt || !last) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastRef.current = pt;
  };

  const end = (e) => {
    if (!drawingRef.current) return;
    e?.preventDefault?.();
    drawingRef.current = false;
    lastRef.current = null;
    emit();
  };

  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div
        className={`relative overflow-hidden border border-[var(--t-border)] bg-[var(--t-bg)] ${
          disabled ? "opacity-60" : ""
        }`}
      >
        <canvas
          ref={canvasRef}
          className="block h-36 w-full touch-none cursor-crosshair sm:h-40"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          aria-label={label}
          data-testid={`${testId}-canvas`}
        />
        {!hasInk && !disabled ? (
          <p
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[var(--t-meta)]"
            aria-hidden
          >
            {label}
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-3">
        <p
          className={`min-h-[1.1rem] text-[11px] tracking-[0.06em] ${
            timestamp ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
          }`}
          data-testid={`${testId}-timestamp`}
        >
          {timestamp || "\u00a0"}
        </p>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)] disabled:opacity-40"
          data-testid={`${testId}-clear`}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

export function isSignatureDataUrl(value) {
  return typeof value === "string" && /^data:image\/(png|jpeg|jpg|webp);base64,/i.test(value);
}
